// CE-04g-UI-live-bar-continuity — pure helpers that compute ET
// (America/New_York) calendar-date keys from real instants. The live-tick
// reducer in src/components/chart/TradeChart.jsx uses these to decide whether
// a new intraday bar opens
// from the previous bar's close (same RTH session — eliminate
// intra-session gap) or from the new tick price (new RTH session —
// preserve a real overnight / weekend / holiday gap).
//
// CRITICAL: the inputs MUST be real instants (Date or real-UTC epoch
// seconds / ms). Do NOT pass "fake-UTC" bar anchors produced by
// floorToBarPeriod / toChartTimestamp — those bake the user's display tz
// into the UTC fields and would misclassify the ET date for any user
// whose display tz ≠ ET.
//
// This module MUST NOT import from src/lib/timezone.js — keep it
// self-contained so the timezone-conversion module can evolve without
// pulling session-boundary semantics with it.

import { boundedMemo } from '../boundedMemo.js'

const ET_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function normalizeRealInstant(input) {
  if (input == null) return null
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input
  if (typeof input !== 'number' || !Number.isFinite(input)) return null

  // The < 1e12 heuristic mirrors normalizeToDate in src/lib/timezone.js
  // (seconds vs ms). 1e12 ms ≈ 2001-09-09, well before any tick we'd see.
  const ms = Math.abs(input) < 1e12 ? input * 1000 : input
  const d = new Date(ms)
  return Number.isNaN(d.getTime()) ? null : d
}

// Indicator updates revisit historical bars on every tick. Retain only the
// immutable instant-to-date result, never candles or calculated VWAP values.
const dateKeyForInstant = boundedMemo(epochMs => {
  const parts = ET_DATE_FORMATTER.formatToParts(new Date(epochMs))
  const get = (type) => parts.find((p) => p.type === type)?.value
  const year = get('year')
  const month = get('month')
  const day = get('day')
  return year && month && day ? `${year}-${month}-${day}` : null
}, 8192)

/**
 * Return the ET (America/New_York) calendar date for a real instant,
 * formatted as YYYY-MM-DD. Returns null for null / undefined / NaN /
 * invalid Date / non-finite number.
 *
 * @param {Date | number | null | undefined} realInstant
 * @returns {string | null}
 */
export function getEtSessionDateKey(realInstant) {
  const date = normalizeRealInstant(realInstant)
  return date ? dateKeyForInstant(date.getTime()) : null
}

/**
 * Return true iff two real instants fall on the same ET calendar date.
 * Returns false if either side is null / invalid.
 *
 * @param {Date | number | null | undefined} prevRealInstant
 * @param {Date | number | null | undefined} nextRealInstant
 * @returns {boolean}
 */
export function isSameRthSession(prevRealInstant, nextRealInstant) {
  const prev = getEtSessionDateKey(prevRealInstant)
  const next = getEtSessionDateKey(nextRealInstant)
  return prev !== null && prev === next
}

// CE-04g-UI-vwap-anchor — additional ET keying helpers consumed by
// calcSessionVWAP / calcPivotLevels for ET-correct session bucketing.
// All helpers accept real instants (Date | epoch s/ms) — never the fake-UTC
// `time` field LWC consumes. Callers are responsible for passing the
// candle's `rawEpochMs`.

function parseYmd(key) {
  // key is YYYY-MM-DD; compute a UTC epoch ms for the ET calendar date so we
  // can do day arithmetic with stable math (no DST drift on the day index).
  const y = Number(key.slice(0, 4))
  const mo = Number(key.slice(5, 7))
  const d = Number(key.slice(8, 10))
  return Date.UTC(y, mo - 1, d)
}

function ymdFromUtcEpoch(ms) {
  const d = new Date(ms)
  const y = d.getUTCFullYear()
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

// Return the ISO weekday for a YYYY-MM-DD key. 1 = Monday … 7 = Sunday.
function isoWeekdayFromKey(key) {
  const day = new Date(parseYmd(key)).getUTCDay() // 0 = Sun
  return day === 0 ? 7 : day
}

function isoMondayKeyFromKey(key) {
  const isoDow = isoWeekdayFromKey(key) // 1..7
  const ms = parseYmd(key) - (isoDow - 1) * 86400000
  return ymdFromUtcEpoch(ms)
}

function addDaysToKey(key, days) {
  return ymdFromUtcEpoch(parseYmd(key) + days * 86400000)
}

/**
 * Return the ET (America/New_York) calendar month for a real instant,
 * formatted as YYYY-MM. Returns null on invalid input.
 *
 * @param {Date | number | null | undefined} realInstant
 * @returns {string | null}
 */
export function getEtMonthKey(realInstant) {
  const key = getEtSessionDateKey(realInstant)
  return key ? key.slice(0, 7) : null
}

/**
 * Return the ET (America/New_York) calendar year for a real instant,
 * formatted as YYYY. Returns null on invalid input.
 *
 * @param {Date | number | null | undefined} realInstant
 * @returns {string | null}
 */
export function getEtYearKey(realInstant) {
  const key = getEtSessionDateKey(realInstant)
  return key ? key.slice(0, 4) : null
}

/**
 * Stable 2-day bucket key anchored to the ISO trading week. Two ET dates
 * with the same `floor((isoDow-1)/2)` AND the same ISO Monday key share a
 * bucket — i.e., Mon-Tue, Wed-Thu, Fri-Sat (rare; markets closed) all
 * bucket together. Reproducible regardless of dataset start because the
 * anchor is derived from the candle's own ISO weekday.
 *
 * @param {Date | number | null | undefined} realInstant
 * @returns {string | null}
 */
export function getEt2DaySessionKey(realInstant) {
  const key = getEtSessionDateKey(realInstant)
  if (!key) return null
  const isoMondayKey = isoMondayKeyFromKey(key)
  const isoDow = isoWeekdayFromKey(key) // 1..7
  const bucket = Math.floor((isoDow - 1) / 2)
  return `${isoMondayKey}#${bucket}`
}

function readHolidaySet(tradingCalendar) {
  if (!tradingCalendar) return null
  const raw = tradingCalendar.holidays
  if (!Array.isArray(raw) || raw.length === 0) return null
  const out = new Set()
  for (const h of raw) {
    if (typeof h === 'string') out.add(h)
    else if (h && typeof h.date === 'string') out.add(h.date)
  }
  return out.size === 0 ? null : out
}

/**
 * Walk one ET calendar day at a time back from `etDateKey`, skipping
 * weekends and any dates present in `tradingCalendar.holidays`. Returns
 * the resulting YYYY-MM-DD key, or null if the 14-iteration safety cap
 * is hit (defensive — should not fire under any real NYSE calendar).
 *
 * CE-04g-UI-vwap-window: used by the chart-layer 2-day rolling VWAP
 * window to anchor on the previous real trading day. When the cap
 * returns null, callers degrade gracefully to a 1-day window.
 *
 * @param {string} etDateKey  YYYY-MM-DD ET calendar date
 * @param {{ holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {string | null}
 */
export function getPreviousEtTradingDayKey(etDateKey, tradingCalendar) {
  if (typeof etDateKey !== 'string' || etDateKey.length < 10) return null
  const holidays = readHolidaySet(tradingCalendar)
  let candidate = etDateKey
  for (let i = 0; i < 14; i++) {
    candidate = addDaysToKey(candidate, -1)
    const dow = new Date(parseYmd(candidate)).getUTCDay() // 0 = Sun, 6 = Sat
    if (dow === 0 || dow === 6) continue
    if (holidays && holidays.has(candidate)) continue
    return candidate
  }
  return null
}

/**
 * Return the ISO-week anchor key for a real instant — the YYYY-MM-DD of the
 * first real trading day of the current ISO week. When `tradingCalendar` is
 * null/empty/missing-holidays, falls back to naive ISO Monday.
 *
 * @param {Date | number | null | undefined} realInstant
 * @param {{ id?: string, holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {string | null}
 */
export function getEtWeekAnchorKey(realInstant, tradingCalendar) {
  const key = getEtSessionDateKey(realInstant)
  return getEtWeekAnchorKeyFromDateKey(key, tradingCalendar)
}

/**
 * Return the ISO-week anchor key for an already-derived ET calendar date key.
 * Use this for date-anchored daily bars whose IBKR YYYYMMDD key is already the
 * session date and must not be reinterpreted as a UTC-midnight instant.
 *
 * @param {string | null | undefined} etDateKey  YYYY-MM-DD
 * @param {{ id?: string, holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {string | null}
 */
export function getEtWeekAnchorKeyFromDateKey(etDateKey, tradingCalendar) {
  if (typeof etDateKey !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(etDateKey)) return null
  const isoMonday = isoMondayKeyFromKey(etDateKey)
  const holidays = readHolidaySet(tradingCalendar)
  if (!holidays) return isoMonday

  // Walk Mon → Tue → Wed → … through Sunday, returning the first non-holiday.
  // If every day in the ISO week is a holiday (degenerate), fall back to ISO
  // Monday.
  let candidate = isoMonday
  for (let i = 0; i < 7; i++) {
    if (!holidays.has(candidate)) return candidate
    candidate = addDaysToKey(candidate, 1)
  }
  return isoMonday
}
